// ============================================================
// ExpressJwtAuthentication.jsx
//
// A branded, responsive course content page for the 
// "JWT Authentication" lesson.
//
// Author: ProDev Security Series
// Features: Stateless auth patterns, token signing/verification,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import VpnKeyIcon             from "@mui/icons-material/VpnKey";
import EnhancedEncryptionIcon from "@mui/icons-material/EnhancedEncryption";
import ShieldIcon             from "@mui/icons-material/Shield";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ContentCopyIcon       from "@mui/icons-material/ContentCopy";
import DoneIcon              from "@mui/icons-material/Done";
import SchoolIcon            from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon         from "@mui/icons-material/ArrowBack";
import CodeIcon              from "@mui/icons-material/Code";
import LockPersonIcon        from "@mui/icons-material/LockPerson";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}>// {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function ExpressJwtAuthentication() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Security · Module 13"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        JWT <span>Authentication</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        JSON Web Tokens (JWT) are the standard for stateless session management. At ProDev, we use JWTs to securely pass user identity across distributed systems without needing to store sessions in memory.
                    </Typography>
                </Box>

                {/* 1. SETUP */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<TerminalIcon />} label="1. Installation & Config" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Install the library and define a secret key. In production, always pull this secret from environment variables.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const jwt = require('jsonwebtoken');", comment: "npm install jsonwebtoken" },
                        { cmd: "const SECRET = process.env.JWT_SECRET;", comment: "Load from secure .env file" },
                    ]} />
                </Box>

                {/* 2. SIGNING */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<EnhancedEncryptionIcon />} label="2. Issuing Tokens (Sign)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        When a user successfully logs in, generate a token containing non-sensitive payload data.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "app.post('/login', (req, res) => {", comment: "" },
                        { cmd: "  // ...authenticate user logic here...", comment: "" },
                        { cmd: "  const payload = { id: 1, email: 'hazzan@prodev.com' };", comment: "The data to be encoded" },
                        { cmd: "  const token = jwt.sign(payload, SECRET, { expiresIn: '1h' });", comment: "" },
                        { cmd: "  res.json({ token });", comment: "Send back to client" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                {/* 3. VERIFICATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<LockPersonIcon />} label="3. Protecting Routes (Verify)" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Create a middleware that extracts the token from the <code style={{color: BRAND.white}}>Authorization</code> header and validates its signature.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "const authenticateToken = (req, res, next) => {", comment: "" },
                        { cmd: "  const token = req.headers['authorization']?.split(' ')[1];", comment: "Extract Bearer Token" },
                        { cmd: "  if (!token) return res.sendStatus(401);", comment: "" },
                        { cmd: "", comment: "" },
                        { cmd: "  jwt.verify(token, SECRET, (err, decoded) => {", comment: "Validate signature" },
                        { cmd: "    if (err) return res.sendStatus(403);", comment: "Expired or invalid" },
                        { cmd: "    req.user = decoded;", comment: "Attach user data to request" },
                        { cmd: "    next();", comment: "" },
                        { cmd: "  });", comment: "" },
                        { cmd: "};", comment: "" },
                    ]} />
                </Box>

                {/* 4. USAGE */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<ShieldIcon />} label="4. Using the Identity" />
                    <CodeBlock lines={[
                        { cmd: "app.get('/profile', authenticateToken, (req, res) => {", comment: "Protected Endpoint" },
                        { cmd: "  res.send(`Identity confirmed for: ${req.user.email}`);", comment: "" },
                        { cmd: "});", comment: "" },
                    ]} />
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* OUTCOMES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<StarIcon />} label="Learning Outcomes" />
                    <Grid container spacing={1.5}>
                        {[
                            "Implement stateless token-based authentication logic",
                            "Utilize JWT signatures to prevent client-side tampering",
                            "Extract and validate Bearer tokens from HTTP headers",
                            "Manage token lifecycles using expiration times",
                            "Develop reusable middleware to protect private API routes",
                            "Differentiate between Token signing and data Encryption",
                        ].map((item) => (
                            <Grid item xs={12} sm={6} key={item}>
                                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
                                    <CheckCircleOutlineIcon sx={{ color: BRAND.gold, fontSize: 19, mt: 0.1 }} />
                                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>{item}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> JWTs are encoded, not encrypted. Anyone can decode the payload at <strong>jwt.io</strong>. Never store sensitive info like passwords or credit card numbers inside the token itself!
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
